"use client";

import { CustomButton } from "@/components/ui/custom-button";
import { Link } from "@/i18n/navigation";
import { motion } from "motion/react";
import { DemoModal } from "@/components/shared/DemoModal";
import { AnimatedLMSText } from "@/components/shared/AnimatedLMSText";

interface FeatureHeroSectionProps {
	icon: string;
	iconAlt: string;
	category: string;
	title: string;
	highlightedText: string;
	description: string;
	buttonText?: string;
	buttonHref?: string;
	secondButtonText?: string;
	secondButtonHref?: string;
	showDemoModal?: boolean;
	backgroundImage?: string;
	backgroundImageAlt?: string;
	mobileImage?: string;
	mobileImageAlt?: string;
	onClick?: () => void;
}

export function FeatureHeroSection({
	icon,
	iconAlt,
	category,
	title,
	highlightedText,
	description,
	buttonText = "Book an intro",
	buttonHref = "/book-a-demo",
	secondButtonText,
	secondButtonHref,
	showDemoModal = true,
	backgroundImage = "/apple_images.png",
	backgroundImageAlt = "StudyStash Dashboard shown on a MacBook Pro, iPad and iPhone",
	mobileImage = "/apple_image_mobile.png",
	mobileImageAlt = "StudyStash Dashboard shown on a MacBook Pro",
	onClick
}: FeatureHeroSectionProps) {
	return (
		<div className="my-16 flex w-full relative">
			{/* Background SVG - Positioned relative to section */}
			{showDemoModal && (
				<div className="absolute inset-0 z-0 pointer-events-none">
					<img 
						src="/top_line.svg" 
						alt="Illustration of the StudyStash teaching-learning loop" 
						className="absolute w-full h-[1000px] object-cover opacity-100"
						style={{
							top: '-15%',
							left: '0%',
							transform: 'scale(1.0)',
							objectPosition: 'left center'
						}}
					/>
				</div>
			)}
			<div className="container mx-auto px-4 lg:px-8 relative z-10">
				<div className="relative flex flex-col lg:flex-row lg:items-start">
					{/* Left side - Text content */}
					<div className="flex flex-col lg:w-2/3 lg:pr-8">
						<div className="flex flex-col justify-start pb-8 text-left leading-[50px] lg:leading-[70px] md:leading-[60px]">
							{/* Category and Icon */}
							{(icon || category) && (
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									className="mb-4 flex flex-row items-center space-x-2"
								>
									{icon && (
										<div className="flex items-center justify-center">
											<img 
												src={icon}
												alt={iconAlt}
												className="rounded-xl border-2 border-b-4 border-gray-200" 
												width={36} 
											/>
										</div>
									)}
									{category && (
										<p className="pt-1.5 pl-1 font-bold uppercase text-black leading-none">{category}</p>
									)}
								</motion.div>
							)}

							{/* Main Title */}
							<motion.h1
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.2 }}
								className="text-4xl hidden sm:block font-bold leading-tight text-gray-800 lg:text-5xl md:text-[42px]"
							>
								{title}
								<br />
                                {highlightedText === "all within your LMS" ? (
                                    <span className="text-black inline-flex items-center">
                                        all within your LMS  <AnimatedLMSText />
                                    </span>
                                ) : (
                                    <span className="text-black">{highlightedText}</span>
                                )}
							</motion.h1>

							{/* Main Title */}
							<motion.h1
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.2 }}
								className="text-4xl block sm:hidden font-bold leading-tight text-gray-800 lg:text-5xl md:text-[42px]"
							>
								
									{title} all within your LMS  <span className="relative top-[6px]"><AnimatedLMSText /></span>
							</motion.h1>
						</div>

						{/* Description */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.4 }}
							className="hidden max-w-xl text-left sm:flex sm:flex-col"
						>
							<p className="text-base text-black md:text-lg">
								{description}
							</p>
						</motion.div>

						{/* Mobile Description */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.4 }}
							className="flex max-w-3xl text-left sm:hidden"
						>
							<p className="text-base text-black md:text-lg">
								{description}
							</p>
						</motion.div>

						{/* Action Buttons */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.6 }}
							className="flex flex-row items-start pt-8 space-x-2"
						>
				{onClick ? (
					<CustomButton 
						variant="primary" 
						size="lg"
						onClick={onClick}
					>
						{buttonText}
					</CustomButton>
				) : (
								<>
									<Link href={buttonHref}>
										<CustomButton variant="primary" size="lg">
											{buttonText}
										</CustomButton>
									</Link>
									{secondButtonText && secondButtonHref && (
										<Link href={secondButtonHref}>
											<CustomButton variant="outline" size="lg">
												{secondButtonText}
											</CustomButton>
										</Link>
									)}
								</>
							)}
							{showDemoModal && <DemoModal type="xl" />}
						</motion.div>
					</div>
					
						{/* Right side - Images */}
						<div className="lg:w-[65%] lg:absolute lg:bottom-[-200px] lg:right-0 relative z-10">
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.8 }}
								className="relative z-10 w-full max-w-[600px] pt-10 sm:max-w-[900px] lg:pt-0"
							>
								<picture>
									<source media="(min-width: 640px)" srcSet={backgroundImage} />
									<img
										src={mobileImage}
										alt={mobileImageAlt || backgroundImageAlt}
										className="w-full object-contain"
										decoding="async"
										fetchPriority="high"
									/>
								</picture>
							</motion.div>
						</div>
				</div>
			</div>
		</div>
	);
}
